import {
  // Type returned by the backend
  Menu as MenuType,
  MenuStatus as MenuStatusType,
  ItemStatus as ItemStatusType,
  ItemState as ItemStateType,
  Items as ItemsType,
  Entity as EntityType,
  ModifiersGroup as ModifiersGroupType,
} from 'data/types/menu';

export type ItemStatus = ItemStatusType;
export type ItemState = ItemStateType;
export type Item = {
  itemId: string;
  name: {
    displayName: string;
    printName: string;
  };
  price: number;
  description?: string;
  printDescription?: string;
  status: ItemStatus;
  state?: ItemState;
  isUpdatingStatus?: boolean;
  imageUrl: string | null;
  imageUrlSmall: string | null;
  imageUrlMedium: string | null;
};
export type Items = Record<Item['itemId'], Item>;
const normalizeItems = (items: ItemsType) =>
  Object.keys(items).reduce<Items>(
    (acum, itemKey) => ({
      ...acum,
      [itemKey]: {
        itemId: items[itemKey].itemId,
        name: {
          displayName: items[itemKey].name.displayName,
          printName: items[itemKey].name.printName,
        },
        price: parseInt(items[itemKey].price, 10),
        description: items[itemKey].description,
        printDescription: items[itemKey].printDescription,
        status: items[itemKey].status,
        state: items[itemKey].state,
        imageUrl: items[itemKey].imageUrl,
        imageUrlSmall: items[itemKey].imageUrlSmall,
        imageUrlMedium: items[itemKey].imageUrlMedium,
      },
    }),
    {},
  );

export type Modifier = {
  id: string;
  name: string;
  usedIn: Item['itemId'][];
  items: Item['itemId'][];
  choices: {
    maximum: number;
    minimum: number;
  };
};
export type ModifiersGroup = Record<Modifier['id'], Modifier>;
const normalizeModifiers = (modifiers: ModifiersGroupType) =>
  Object.keys(modifiers).reduce<ModifiersGroup>(
    (acum, modifierId) => ({
      ...acum,
      [modifierId]: {
        id: modifiers[modifierId].id,
        name: modifiers[modifierId].name,
        usedIn: modifiers[modifierId].usedIn,
        items: modifiers[modifierId].items,
        choices: {
          maximum: parseInt(modifiers[modifierId].choices.maximum, 10),
          minimum: parseInt(modifiers[modifierId].choices.minimum, 10),
        },
      },
    }),
    {},
  );

export type MenuStatus = MenuStatusType;
export const normalizeMenu = (data: MenuType) => {
  return {
    id: data.id,
    brandId: data.brandId,
    structure: data.structure,
    modifierGroups: normalizeModifiers(data.modifierGroups),
    items: normalizeItems(data.items),
    status: data.status,
  };
};
// Final type expected by the endpoint
export type Menu = ReturnType<typeof normalizeMenu>;

export type Category = {
  id: string;
  name: string;
};
export type Entity = EntityType;
export type ModalItem = Item & {
  childrenEntities: Entity[];
  categoryId: string;
};
export type SelectOption = {
  value: string;
  label: string;
};
export type TemporalItem = {
  id: string;
  name: string;
  price: number;
  status: ItemStatus;
  state?: ItemState;
  imageUrl: string | null;
  imageUrlSmall: string | null;
  imageUrlMedium: string | null;
};
export type ModalModifier = Modifier & {
  temporalItems: TemporalItem[];
};
// Type used in the view
export type MenuLocal = Menu & {
  modal?: {
    category?: {
      previousData?: Category;
    };
    item?: {
      previousData: ModalItem;
      categoryOptions: SelectOption[];
    };
    modifier?: {
      previousData: ModalModifier;
    };
  };
  hasChanges?: boolean;
};

export const normalizeChoices = (
  choices: ModalModifier['choices'],
  totalItems: number,
) => {
  const max = choices.maximum;
  const min = choices.minimum;
  let type = max === 1 ? 'Single Select' : 'Multi Select';
  let selection = min === 0 ? 'Optional' : 'Required';

  if ((max > 1 && max < totalItems) || min > 1) {
    type = `Custom (max = ${max})`;
    selection = `Custom (min = ${min})`;
  }
  return {
    type,
    selection,
  };
};

export const statusOptions = [
  { label: 'Available', value: 'ACTIVE' },
  { label: 'Paused for today', value: 'SUSPEND_DAY' },
  { label: 'Paused indefinitely', value: 'SUSPEND_INDEFINITE' },
];
export const normalizeStatus = (value: string) =>
  statusOptions.find((status) => status.value === value)?.label || undefined;
